class AraPickupCalendar extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    this.dateInput = this.querySelector('[data-ara-calendar-date-input]');
    this.dateButton = this.querySelector('[data-ara-calendar-toggle]');
    this.dateDisplay = this.querySelector('[data-ara-calendar-date-display]');
    this.calendar = this.querySelector('[data-ara-calendar-popover]');
    this.calendarGrid = this.querySelector('[data-ara-calendar-grid]');
    this.calendarMonth = this.querySelector('[data-ara-calendar-month]');
    this.previousButton = this.querySelector('[data-ara-calendar-prev]');
    this.nextButton = this.querySelector('[data-ara-calendar-next]');
    this.timeInput = this.querySelector('[data-ara-calendar-time-input]');
    this.locationInput = this.querySelector('[data-ara-calendar-location-input]');
    this.saveButton = this.querySelector('[data-ara-calendar-save]');
    this.error = this.querySelector('[data-ara-calendar-error]');
    this.summary = this.querySelector('[data-ara-calendar-summary]');

    if (!this.dateInput || !this.dateButton || !this.calendar || !this.calendarGrid) return;

    this.storageKey = this.dataset.storageKey || 'araPickupSelection';
    this.minLeadDays = Number(this.dataset.minLeadDays || 0);
    this.maxAdvanceDays = Number(this.dataset.maxAdvanceDays || 60);
    this.syncCart = this.dataset.syncCart === 'true';
    this.autoSave = this.dataset.autoSave === 'true';
    this.unavailableMessage = this.dataset.unavailableMessage || 'This pickup date is unavailable. Please choose another date.';
    this.blockedDates = new Set(this.parseList(this.dataset.blockedDates).filter((value) => /^\d{4}-\d{2}-\d{2}$/.test(value)));
    this.blockedWeekdays = new Set(this.parseList(this.dataset.blockedWeekdays).map(Number));

    const configuredDays = this.parseList(this.dataset.allowedDays).map(Number);
    this.allowedDays = new Set(
      configuredDays.length
        ? configuredDays
        : [0, 1, 2, 3, 4, 5, 6].filter((day) => !this.blockedWeekdays.has(day))
    );

    const slotsScript = this.querySelector('[data-ara-calendar-slots]');
    let slots = {};

    try {
      slots = JSON.parse(slotsScript?.textContent || '{}');
    } catch (error) {
      slots = {};
    }

    this.weekdaySlots = this.parseLines(slots.weekday);
    this.saturdaySlots = this.parseLines(slots.saturday);
    this.setDateLimits();
    this.bindEvents();
    this.loadSelection();
    this.attachToNativeProductForm();
    this.renderCalendar();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleDocumentClick);
  }

  parseList(value) {
    return String(value || '')
      .split(/[\s,]+/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  parseLines(value) {
    return String(value || '')
      .split(/\r?\n/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  setDateLimits() {
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    this.minDate = this.addDays(today, this.minLeadDays);
    this.maxDate = this.addDays(today, this.maxAdvanceDays);
    const firstAvailable = this.findNextAvailableDate(this.minDate);
    this.calendarViewDate = new Date(firstAvailable.getFullYear(), firstAvailable.getMonth(), 1, 12);
  }

  bindEvents() {
    this.dateButton.addEventListener('click', () => this.toggleCalendar());
    this.previousButton?.addEventListener('click', () => this.changeMonth(-1));
    this.nextButton?.addEventListener('click', () => this.changeMonth(1));
    this.calendarGrid.addEventListener('click', (event) => {
      const button = event.target.closest('[data-ara-calendar-day]');
      if (button && !button.disabled) this.selectDate(button.dataset.date || '');
    });

    this.timeInput?.addEventListener('change', () => this.handleSelectionChange());
    this.locationInput?.addEventListener('change', () => this.handleSelectionChange());
    this.saveButton?.addEventListener('click', () => {
      if (this.validate(true)) this.persistSelection(true);
    });
    document.addEventListener('click', this.handleDocumentClick);
  }

  handleDocumentClick = (event) => {
    if (!this.calendar.hidden && !this.contains(event.target)) this.closeCalendar();
  };

  addDays(date, amount) {
    const result = new Date(date);
    result.setDate(result.getDate() + amount);
    result.setHours(12, 0, 0, 0);
    return result;
  }

  isDateAllowed(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return false;
    const check = new Date(date);
    check.setHours(12, 0, 0, 0);
    const value = this.toInputDate(check);
    return check >= this.minDate && check <= this.maxDate && this.allowedDays.has(check.getDay()) && !this.blockedDates.has(value);
  }

  findNextAvailableDate(startDate) {
    const date = new Date(startDate);
    date.setHours(12, 0, 0, 0);

    while (date <= this.maxDate) {
      if (this.isDateAllowed(date)) return date;
      date.setDate(date.getDate() + 1);
    }

    return new Date(this.minDate);
  }

  getSlotsForDate(date) {
    if (!this.isDateAllowed(date)) return [];
    if (date.getDay() === 6 && this.saturdaySlots.length) return this.saturdaySlots;
    return this.weekdaySlots;
  }

  toggleCalendar() {
    if (this.calendar.hidden) this.openCalendar();
    else this.closeCalendar();
  }

  openCalendar() {
    const selectedDate = this.dateInput.value ? this.fromInputDate(this.dateInput.value) : null;
    const startingDate = selectedDate && this.isDateAllowed(selectedDate)
      ? selectedDate
      : this.findNextAvailableDate(this.minDate);

    this.calendarViewDate = new Date(startingDate.getFullYear(), startingDate.getMonth(), 1, 12);
    this.renderCalendar();
    this.calendar.hidden = false;
    this.dateButton.setAttribute('aria-expanded', 'true');
  }

  closeCalendar() {
    this.calendar.hidden = true;
    this.dateButton.setAttribute('aria-expanded', 'false');
  }

  changeMonth(direction) {
    this.calendarViewDate = new Date(
      this.calendarViewDate.getFullYear(),
      this.calendarViewDate.getMonth() + direction,
      1,
      12
    );
    this.renderCalendar();
  }

  renderCalendar() {
    const year = this.calendarViewDate.getFullYear();
    const month = this.calendarViewDate.getMonth();
    const firstDay = new Date(year, month, 1, 12);
    const lastDay = new Date(year, month + 1, 0, 12);

    this.calendarMonth.textContent = new Intl.DateTimeFormat(
      document.documentElement.lang || 'en-US',
      { month: 'long', year: 'numeric' }
    ).format(firstDay);

    this.calendarGrid.replaceChildren();

    for (let index = 0; index < firstDay.getDay(); index += 1) {
      const empty = document.createElement('span');
      empty.className = 'ara-pickup-calendar__empty';
      empty.setAttribute('aria-hidden', 'true');
      this.calendarGrid.append(empty);
    }

    for (let day = 1; day <= lastDay.getDate(); day += 1) {
      const date = new Date(year, month, day, 12);
      const value = this.toInputDate(date);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ara-pickup-calendar__day';
      button.dataset.araCalendarDay = '';
      button.dataset.date = value;
      button.textContent = String(day);
      button.disabled = !this.isDateAllowed(date);
      button.setAttribute('aria-label', `${this.formatDate(value)}${button.disabled ? ' — unavailable' : ''}`);

      if (value === this.dateInput.value) {
        button.classList.add('is-selected');
        button.setAttribute('aria-current', 'date');
      }

      this.calendarGrid.append(button);
    }

    const earliestMonth = new Date(this.minDate.getFullYear(), this.minDate.getMonth(), 1, 12);
    const latestMonth = new Date(this.maxDate.getFullYear(), this.maxDate.getMonth(), 1, 12);
    const currentMonth = new Date(year, month, 1, 12);

    if (this.previousButton) this.previousButton.disabled = currentMonth <= earliestMonth;
    if (this.nextButton) this.nextButton.disabled = currentMonth >= latestMonth;
  }

  selectDate(value) {
    const date = this.fromInputDate(value);
    if (!this.isDateAllowed(date)) return;

    const previousTime = this.timeInput?.value || '';
    this.dateInput.value = value;
    this.dateDisplay.textContent = this.formatDate(value);
    this.dateButton.classList.add('has-value');
    this.populateTimeSlots(value, previousTime);
    this.closeCalendar();
    this.clearError();
    this.dispatchFieldEvents(this.dateInput);

    if (this.autoSave && this.isComplete()) this.persistSelection(true);
    this.timeInput?.focus();
  }

  populateTimeSlots(dateValue, selectedValue = '') {
    if (!this.timeInput) return;

    const placeholder = this.timeInput.dataset.placeholder || 'Choose a time';
    const date = dateValue ? this.fromInputDate(dateValue) : null;
    const slots = date ? this.getSlotsForDate(date) : [];
    this.timeInput.replaceChildren(new Option(placeholder, ''));

    slots.forEach((slot) => this.timeInput.append(new Option(slot, slot)));
    this.timeInput.disabled = slots.length === 0;
    this.timeInput.value = selectedValue && slots.includes(selectedValue) ? selectedValue : '';
    this.dispatchFieldEvents(this.timeInput);
  }

  handleSelectionChange() {
    this.clearError();
    if (this.autoSave && this.isComplete()) this.persistSelection(true);
    else this.syncNativeProductFields(this.getSelection());
  }

  isComplete() {
    const selection = this.getSelection();
    return Boolean(selection.date && selection.time && selection.location);
  }

  validate(showError = false) {
    const selection = this.getSelection();
    let message = '';

    if (!selection.date) message = 'Please choose a pickup date.';
    else if (!this.isDateAllowed(this.fromInputDate(selection.date))) message = this.unavailableMessage;
    else if (!selection.time) message = 'Please choose a pickup time.';
    else if (!this.getSlotsForDate(this.fromInputDate(selection.date)).includes(selection.time)) message = 'Please choose an available pickup time.';
    else if (!selection.location) message = 'Please choose a pickup location.';

    if (message && showError) {
      this.showError(message);
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    return !message;
  }

  getSelection() {
    return {
      date: this.dateInput?.value || '',
      time: this.timeInput?.value || '',
      location: this.locationInput?.value || this.dataset.location || '',
    };
  }

  loadSelection() {
    let selection = null;

    try {
      selection = JSON.parse(localStorage.getItem(this.storageKey) || 'null');
    } catch (error) {
      selection = null;
    }

    if (selection?.date && this.isDateAllowed(this.fromInputDate(selection.date))) {
      this.dateInput.value = selection.date;
      this.dateDisplay.textContent = this.formatDate(selection.date);
      this.dateButton.classList.add('has-value');
      this.populateTimeSlots(selection.date, selection.time || '');

      if (this.locationInput && selection.location) {
        const hasLocation = [...this.locationInput.options || []].some((option) => option.value === selection.location);
        if (this.locationInput.tagName !== 'SELECT' || hasLocation) this.locationInput.value = selection.location;
      }

      if (this.isComplete()) this.updateSummary(selection, false);
    } else {
      this.populateTimeSlots('');
    }
  }

  async persistSelection(announce = false) {
    const selection = this.getSelection();
    if (!this.isComplete()) return;

    localStorage.setItem(this.storageKey, JSON.stringify(selection));
    this.syncNativeProductFields(selection);
    this.updateSummary(selection, announce);

    document.dispatchEvent(new CustomEvent('ara:pickup-saved', { detail: selection }));

    if (this.syncCart) {
      try {
        await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            attributes: {
              'Pickup date': selection.date,
              'Pickup time': selection.time,
              'Pickup location': selection.location,
            },
          }),
        });
      } catch (error) {
      }
    }
  }

  updateSummary(selection, announce) {
    if (!this.summary) return;
    const date = this.summary.querySelector('[data-ara-calendar-summary-date]');
    const time = this.summary.querySelector('[data-ara-calendar-summary-time]');
    const location = this.summary.querySelector('[data-ara-calendar-summary-location]');
    const message = this.summary.querySelector('[data-ara-calendar-summary-message]');

    if (date) date.textContent = this.formatDate(selection.date);
    if (time) time.textContent = selection.time;
    if (location) location.textContent = selection.location;
    if (message && announce) message.textContent = 'Pickup saved';
    this.summary.hidden = false;
  }

  attachToNativeProductForm() {
    if (this.dataset.attachToProductForm !== 'true') return;
    const section = this.closest('[id^="shopify-section-"]');
    this.nativeProductForm = section?.querySelector('form[data-type="add-to-cart-form"]') || null;
    if (!this.nativeProductForm) return;

    const standardWrapper = this.closest('.ara-standard-pickup');
    const buyButtonsBlock = this.nativeProductForm.closest('product-form-component')?.closest('.buy-buttons-block');
    if (standardWrapper && buyButtonsBlock) buyButtonsBlock.before(standardWrapper);

    this.syncNativeProductFields(this.getSelection());
    this.nativeProductForm.addEventListener('submit', (event) => {
      if (this.validate(true)) {
        this.persistSelection(false);
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
    }, true);
  }

  syncNativeProductFields(selection) {
    if (!this.nativeProductForm) return;
    const fields = {
      'Pickup date': selection.date,
      'Pickup time': selection.time,
      'Pickup location': selection.location,
    };

    Object.entries(fields).forEach(([name, value]) => {
      let input = this.nativeProductForm.querySelector(`[data-ara-native-property="${name}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${name}]`;
        input.dataset.araNativeProperty = name;
        this.nativeProductForm.append(input);
      }
      input.value = value || '';
    });
  }

  dispatchFieldEvents(field) {
    if (!field) return;
    field.dispatchEvent(new Event('input', { bubbles: true }));
    field.dispatchEvent(new Event('change', { bubbles: true }));
  }

  showError(message) {
    if (!this.error) return;
    this.error.textContent = message;
    this.error.hidden = false;
  }

  clearError() {
    if (this.error) this.error.hidden = true;
  }

  formatDate(value) {
    return new Intl.DateTimeFormat(
      document.documentElement.lang || 'en-US',
      { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }
    ).format(this.fromInputDate(value));
  }

  fromInputDate(value) {
    const [year, month, day] = String(value || '').split('-').map(Number);
    return new Date(year, month - 1, day, 12);
  }

  toInputDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}

if (!customElements.get('ara-pickup-calendar')) {
  customElements.define('ara-pickup-calendar', AraPickupCalendar);
}
